import React, { useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { CloseIcon } from '@chakra-ui/icons';
import { useForm, useWatch } from 'react-hook-form';
import { Box, HStack, Grid, GridItem, Text, useDisclosure, VStack } from '@chakra-ui/react';

//common components
import FormButton from '../../../common/FormButton';
import DialogueBox from '../../../common/DialogueBox';
import SearchSection from '../../../common/SearchSection';
import MembershipFormWrapper from '../../../common/membershipFormComponents/MembershipFormWrapper';
import SelectCertificateSection from '../../commonSections/SelectCertificateSection';
//form sections
import RequiredInfoSection, {
  requiredInfoFieldsValidation,
} from '../Verification/RequiredInfoSection';
import GeneralInfoSection, { generalInfoFieldsValidation } from './GeneralInfoSection';

//actions & api
import { notify } from '../../../../store/actions/globalActions';
import { FETCH_DUTY_APPLICATIONS } from '../../../../store/actions/actionTypes';
import { getConversionRate, getConversionRates } from '../../../../api/currency';
import {
  applyForCertificate,
  assignPreSoldCertificate,
  getAllApplications,
  getUnusedCertificates,
} from '../../../../api/certificates';

//utils & constants
import _ from 'lodash';
import { formatDate } from '../../../../util/formatUtils';
import { useYupValidationResolver } from '../../../../util/yupUtils';
import AssignCertificate from '../../commonSections/AssignCertificate';
import ApplicationInfoSection, {
  applicationInfoFieldsValidation,
} from '../../commonSections/ApplicationInfoSection';

const ApplyForDutyExemption = () => {
  const millErrorRef = useRef();
  const dispatch = useDispatch();
  const [modal, setModal] = useState();
  const assignCertificateRef = useRef();
  const [certificate, setCertificate] = useState();
  const [formData, setFormData] = useState();
  const [conversions, setConversions] = useState();
  const [selectedMill, setSelectedMill] = useState();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [certErrorMsg, setCertErrorMsg] = useState('');
  const [millUnusedCerts, setMillUnusedCerts] = useState();
  const [certificateToggle, setCertificateToggle] = useState(false);
  const [modalButtonLoading, setModalButtonLoading] = useState(false);

  const fetchUnusedCertificates = async () => {
    setMillUnusedCerts();
    const res = await getUnusedCertificates({ type: 'duty', millId: selectedMill?.id });
    if (res.data?.status === 200) {
      setMillUnusedCerts(res.data?.certificates);
    }
  };

  useEffect(() => {
    (async () => {
      const res = await getConversionRates();
      setConversions(res?.data);
    })();
  }, []);

  useEffect(() => {
    if (selectedMill?.id) {
      fetchUnusedCertificates();
      setValue('processedBy', `${selectedMill?.millName}, ${selectedMill?.millAddress}`);
    }
    setCertificate();
  }, [selectedMill?.id, certificateToggle]);

  useEffect(() => {
    if (certificate?.invoiceId) {
      setCertErrorMsg('');
    } else {
      setCertErrorMsg('Please Select an Invoice');
    }
  }, [certificate]);

  const resolver = useYupValidationResolver(
    yup.object().shape({
      ...generalInfoFieldsValidation,
      ...requiredInfoFieldsValidation,
      ...applicationInfoFieldsValidation,
    }),
  );

  const {
    reset,
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver,
    defaultValues: { issueDate: new Date() },
  });

  const value = useWatch({ control, name: 'value' });
  const currency = useWatch({ control, name: 'currency' });
  const conversionRate = useWatch({ control, name: 'conversionRate' });
  const selectedCertCategory = useWatch({ control, name: 'category' });
  const selectedHsCodesInComma = useWatch({ control, name: 'hsCode' });

  useEffect(() => {
    setValue('conversionRate', getConversionRate(currency, conversions));
  }, [currency]);

  useEffect(() => {
    if (value && currency && conversionRate) {
      setValue(
        'total',
        Math.ceil(parseFloat(parseFloat(value || 0) * parseFloat(conversionRate)).toFixed(2)),
      );
    } else {
      setValue('total', parseFloat(value || 0).toFixed(2));
    }
  }, [conversionRate, value]);

  const onSubmit = (data) => {
    let formdata = {
      ...data,
      millId: selectedMill?.id,
      invoiceId: certificate?.invoiceId,
      blDate: formatDate(data?.blDate),
      dateOfLc: formatDate(data?.dateOfLc),
      issueDate: formatDate(data?.issueDate),
      expiryDate: formatDate(data?.expiryDate),
      invoiceDate: formatDate(data?.invoiceDate),
    };

    let normalizedFormData = _.mapKeys(formdata, (value, key) => {
      if (value) {
        return key;
      }
    });

    setFormData(normalizedFormData);
    setModal('CONFIRM_APPLICATION');
    onOpen();
  };

  const confirmSubmit = async () => {
    const res = await applyForCertificate({ type: 'duty', formData });
    if (res.data?.status === 200) {
      dispatch(
        notify({
          title: 'Success',
          description: res.data.msg,
          status: 'success',
          duration: 5000,
        }),
      );
      let resetObj = {};
      _.mapKeys(formData, (value, key) => (resetObj[key] = ''));
      reset({ ...resetObj, issueDate: new Date() });
      const response = await getAllApplications({
        type: 'duty',
        params: { page: 1, pageSize: 10 },
      });
      if (response.data?.status === 200) {
        dispatch({ type: FETCH_DUTY_APPLICATIONS, payload: response.data });
      }
      setCertificateToggle(!certificateToggle);
    }
  };

  const handleAssignCert = async (data) => {
    const res = await assignPreSoldCertificate({
      millId: selectedMill?.id,
      certificateNum: data?.certificateNum,
      type: 'DUTY',
    });
    if (res.data?.status === 200) {
      dispatch(
        notify({
          title: 'Success',
          description: res.data.msg,
          status: 'success',
          duration: 5000,
        }),
      );
      fetchUnusedCertificates();
      onClose();
    }
  };

  const renderModal = () => {
    switch (modal) {
      case 'CONFIRM_APPLICATION':
        return (
          <DialogueBox
            close={onClose}
            alertTitle="Confirm"
            mountControl={isOpen}
            performAction={confirmSubmit}
            alertMessage="Are you sure you want to submit these information and verify this application?"
          />
        );
      case 'CERT_NUMBER_FORM':
        return (
          <DialogueBox
            close={onClose}
            onActionKeepOpen
            mountControl={isOpen}
            actionButtonText="Submit"
            cancelButtonText="Cancel"
            alertTitle="Assign Pre-sold Certificate"
            actionButtonLoading={modalButtonLoading}
            performAction={() => assignCertificateRef.current.submit()}>
            <AssignCertificate
              mill={selectedMill}
              certType="duty"
              ref={assignCertificateRef}
              handleQuery={handleAssignCert}
              submitting={setModalButtonLoading}
            />
          </DialogueBox>
        );
    }
  };

  return (
    <Box>
      <SearchSection setSelectedMill={setSelectedMill} showProvisionalsAlso={true} />

      <Grid
        p={4}
        mt={5}
        mx={4}
        gap={3}
        bg="white"
        borderRadius="4px"
        templateColumns="repeat(4, 1fr)">
        <GridItem colSpan={4}>
          <Text fontSize="20px" fontWeight="bold" mb={1}>
            Select Mill & Certificate
          </Text>
        </GridItem>
        <GridItem colSpan={1}>
          <Box
            p={4}
            zIndex={1}
            border="1px"
            bg="secondary"
            borderRadius="4px"
            position="relative"
            borderStyle="dashed"
            borderColor="primary.300">
            {selectedMill?.id ? (
              <CloseIcon
                right={3}
                cursor="pointer"
                position="absolute"
                onClick={() => setSelectedMill()}
              />
            ) : null}
            <Text fontSize="md" color="textSecondary">
              Selected Mill
            </Text>
            <Text fontSize="xl" mt={1.5}>
              {selectedMill?.millName || 'None'}
            </Text>
          </Box>
          {selectedMill ? null : (
            <Text fontSize="sm" color="invalid" ref={millErrorRef} mt={2}>
              Please Select a Mill
            </Text>
          )}
        </GridItem>
        {selectedMill && millUnusedCerts?.length > 0 ? (
          <GridItem colSpan={3}>
            <SelectCertificateSection
              type="DUTY"
              error={certErrorMsg}
              certificates={millUnusedCerts}
              setCertificate={setCertificate}
              defaultValue={certificate?.certID}
            />
          </GridItem>
        ) : selectedMill ? (
          <GridItem
            p={4}
            colSpan={3}
            border="1px"
            bg="secondary"
            borderRadius="4px"
            borderColor="invalid">
            <VStack justifyContent="center" h="100%">
              <Text fontSize="md" color="invalid">
                No unused invoice found for this mill! The member needs to pay the application fee
                for a certificate.
              </Text>
            </VStack>
          </GridItem>
        ) : null}
      </Grid>

      {/* {selectedMill && !certificate ? (
        <FormButton ml={4} onClick={handlePreSoldCert}>
          Assign Pre-sold Certificate
        </FormButton>
      ) : null} */}

      {selectedMill && certificate ? (
        <MembershipFormWrapper formName="Duty Exemption Application" mt={4}>
          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <GridItem colSpan={1} bg="white" borderRadius="4px" p={4}>
              <Box border="1px solid red" borderRadius="4px">
                <ApplicationInfoSection
                  errors={errors}
                  control={control}
                  sectionColumn={2}
                  register={register}
                />
              </Box>
              <GeneralInfoSection
                control={control}
                register={register}
                errors={errors}
                selectedMill={selectedMill}
              />
            </GridItem>
            <GridItem colSpan={1} bg="white" borderRadius="4px" p={4}>
              <RequiredInfoSection
                errors={errors}
                control={control}
                register={register}
                sectionName="Required Information"
                selectedCertCategory={selectedCertCategory}
                selectedHsCodesInComma={selectedHsCodesInComma}
                setValue={setValue}
              />
            </GridItem>
          </Grid>
          <HStack justifyContent="end" alignItems="center" mt={4}>
            <FormButton
              mt={0}
              isLoading={isSubmitting}
              onClick={(event) => {
                event.preventDefault();
                handleSubmit(onSubmit)();
              }}>
              SUBMIT
            </FormButton>
          </HStack>
        </MembershipFormWrapper>
      ) : null}
      {/* <DialogueBox
        close={onClose}
        alertTitle="Confirm"
        mountControl={isOpen}
        performAction={confirmSubmit}
        alertMessage="Are you sure you want to submit these information and verify this application?"
      /> */}
      {renderModal()}
    </Box>
  );
};

export default ApplyForDutyExemption;
